import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { trackEvent, setUserId } from '../utils/analytics';
import { setCanonical } from '../utils/seo';
import FAQ from '../components/FAQ';
import CommunityStats from '../components/CommunityStats';
import TrustBadge from '../components/TrustBadge';

function Login() {
  const [step, setStep] = useState(1); // 1: enter email, 2: enter code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const faqRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/dashboard';
  const { loginWithEmail } = useAuth();

  useEffect(() => { setCanonical('/login'); }, []);

  const toggleFAQ = () => {
    setShowFAQ(prev => {
      const newValue = !prev;
      if (newValue) {
        setTimeout(() => { faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
      }
      return newValue;
    });
  };

  const isValidEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post('/api/auth/send-email-code', { email, isLogin: true });
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post('/api/auth/verify-email-code', { email, code });
      await loginWithEmail(email);
      trackEvent('login', { method: 'email' });
      
      // Check if user is an org member — redirect to org-scoped URL
      if (returnTo === '/dashboard') {
        try {
          const token = localStorage.getItem('accessToken');
          const statusRes = await axios.get('/api/org/onboard/status', {
            headers: { Authorization: `Bearer ${token}` }
          });
          const memberships = statusRes.data.memberships || [];
          if (memberships.length > 0) {
            const m = memberships[0];
            const slug = m.org_slug;
            if (slug) {
              if (m.role === 'admin') {
                navigate(`/org/${slug}/admin`);
              } else {
                navigate(`/org/${slug}/dashboard`);
              }
              return;
            }
          }
        } catch (e) { /* not an org member — continue to dashboard */ }
      }
      navigate(returnTo);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '400px' }}>
        <div className="auth-card">
          <div className="logo">
            <img src="/belong.png" alt="Belong" style={{ height: '64px', marginBottom: '8px' }} />
            <p>Find where you belong, safely</p>
          </div>

          <h2>Welcome back 👋</h2>

          {step === 1 ? (
            <form onSubmit={handleSendCode}>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  We'll email you a verification code 📧
                </small>
              </div>

              {error && <div className="error">{error}</div>}

              <button type="submit" className="btn btn-primary" disabled={loading || !isValidEmail}>
                {loading ? 'Sending...' : 'Send code 📧'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyAndLogin}>
              <div className="form-group">
                <label>Verification Code</label>
                <input
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  required
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Enter the 6-digit code we emailed you
                </small>
              </div>

              {error && <div className="error">{error}</div>}

              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Verifying...' : "Let's go! 🚀"}
              </button>

              <button type="button" className="btn btn-secondary" onClick={() => { setStep(1); setError(''); }}
                style={{ marginTop: '10px' }}>
                ← Back
              </button>
            </form>
          )}

          <div className="link" style={{ marginTop: '10px' }}>
            New here? <Link to={`/register${returnTo !== '/dashboard' ? '?returnTo=' + encodeURIComponent(returnTo) : ''}`}>Join Belong ✨</Link>
          </div>

          <div className="link" style={{ marginTop: '20px' }}>
            <Link to="/help" style={{ color: '#7C3AED', fontWeight: '500' }}>Having trouble? Get Help</Link>
          </div>

          <div className="link" style={{ marginTop: '10px' }}>
            <Link to="/b" style={{ color: '#10B981', fontWeight: '600' }}>🔒 Browse Anonymous Discussions</Link>
          </div>

          <div className="link" style={{ marginTop: '10px' }}>
            <Link to="/privacy" style={{ color: '#7C3AED', fontWeight: '500' }}>🔒 Privacy Policy</Link>
            {' · '}
            <Link to="/terms" style={{ color: '#7C3AED', fontWeight: '500' }}>📜 Terms of Service</Link>
            {' · '}
            <Link to="/about" style={{ color: '#7C3AED', fontWeight: '500' }}>💜 About</Link>
          </div>

          <div className="link" style={{ marginTop: '20px', borderTop: '1px solid #e0e0e0', paddingTop: '20px' }}>
            <button onClick={toggleFAQ}
              style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '15px', textDecoration: 'underline', padding: 0 }}>
              {showFAQ ? '▲ Hide FAQs' : 'Have questions? View FAQs'}
            </button>
          </div>
        </div>

        <TrustBadge variant="compact" />
        <CommunityStats variant="banner" />
        {showFAQ && <div ref={faqRef}><FAQ /></div>}
      </div>
    </div>
  );
}

export default Login;
